import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Paper, Stack, Typography, useTheme } from '@mui/material';

import { useGetOrganizationStructure } from '@/api/generated';
import WarningIcon from '@/assets/icons/warning-red.svg?react';
import StandardPopper from '@/components/StandardPopper/StandardPopper';
import palette from '@/theme/palette';
import typography from '@/theme/typography';

import { buildRecursiveStructure, buildTreeNodeOptions, createNodeMap, TreeNode } from './locationTreeUtils';

interface LocationPathPopperProps {
  locationSlug: string;
  organizationSlug: string;
  useInsideDialog?: boolean;
  children: ReactElement;
}

const LocationPathPopperContent = ({
  locationSlug,
  organizationSlug
}: {
  locationSlug: string;
  organizationSlug: string;
}) => {
  const { t } = useTranslation('common');
  const { data: organizationStructure, isLoading } = useGetOrganizationStructure(organizationSlug, {
    query: {
      enabled: !!organizationSlug
    }
  });

  const recursiveOrganizationStructure = useMemo(
    () => organizationStructure && buildRecursiveStructure(organizationStructure),
    [organizationStructure]
  );

  const nodes = useMemo(
    () => recursiveOrganizationStructure && buildTreeNodeOptions(recursiveOrganizationStructure, true),
    [recursiveOrganizationStructure]
  );
  const nodeMap = useMemo(() => (nodes ? createNodeMap(nodes) : {}), [nodes]);

  const pathLabels = useMemo(() => {
    if (!locationSlug || !nodeMap[locationSlug]) return [];

    const path: React.ReactNode[] = [];
    let currentNode: TreeNode | null = nodeMap[locationSlug];

    while (currentNode) {
      path.unshift(currentNode.label);
      currentNode = currentNode.parentKey ? nodeMap[currentNode.parentKey] : null;
    }

    return path;
  }, [locationSlug, nodeMap]);

  return (
    <Stack direction="column" alignItems="start" gap={0.5} sx={typography.body2}>
      {!isLoading && !pathLabels.length && (
        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          alignItems="center"
          gap={0.5}
          sx={{
            '& .WarningIcon-root': {
              width: 32,
              height: 32
            }
          }}
        >
          <WarningIcon className="WarningIcon-root" />
          <Typography variant="body2">{t('common:error.noPathFound')}</Typography>
        </Stack>
      )}
      {isLoading && !pathLabels?.length && (
        <Stack alignItems="center" width="100%">
          <CircularProgress size={16} />
        </Stack>
      )}
      {pathLabels.map((node, index) => (
        <Stack direction="row" key={index}>
          <Box
            sx={{
              '& .LocationItem-icon': {
                color: 'grey.800',
                width: 20
              },
              '& .LocationItem-label': {
                color: 'text.primary'
              }
            }}
          >
            {node}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

const LocationPathPopper: React.FC<LocationPathPopperProps> = ({
  useInsideDialog,
  locationSlug,
  organizationSlug,
  children
}) => {
  const { spacing } = useTheme();

  return (
    <StandardPopper
      placement="bottom"
      caretEndPosition="50%"
      {...(useInsideDialog && {
        getSx: () => ({ zIndex: 2000 }),
        treeRootSelector: '.MuiDialog-container'
      })}
      content={
        <Paper
          sx={{
            mt: 1,
            p: spacing(0.75, 1.5),
            position: 'relative',
            minWidth: '240px',
            '.StandardPopper-root &': {
              bgcolor: palette.background.default
            }
          }}
        >
          <LocationPathPopperContent locationSlug={locationSlug} organizationSlug={organizationSlug} />
        </Paper>
      }
    >
      {children}
    </StandardPopper>
  );
};

export default LocationPathPopper;
